import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Users, Phone, MessageSquare, Mail, MapPin, Upload, Search, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: "", title: "Asesor Comercial", phone: "", whatsapp_phone: "", email: "",
  center: "", center_id: "", status: "active", order: 0, notes: "",
  accepts_leads: true, working_hours_text: "",
};

export default function SellersPage() {
  const { axiosAuth } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({...EMPTY});

  const fetch = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([axiosAuth().get("/sellers"), axiosAuth().get("/centers")]);
      setSellers(s.data);
      setCenters(c.data);
    } catch { toast.error("Error al cargar"); } finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { fetch(); }, [fetch]);

  const f = (k, v) => setForm(p => ({...p, [k]: v}));

  const save = async () => {
    if (!form.name) { toast.error("Nombre obligatorio"); return; }
    try {
      if (editingId) {
        await axiosAuth().put(`/sellers/${editingId}`, form);
        toast.success("Vendedor actualizado");
      } else {
        await axiosAuth().post("/sellers", form);
        toast.success("Vendedor creado");
      }
      setShowDialog(false);
      setEditingId(null);
      setForm({...EMPTY});
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminar este vendedor?")) return;
    try {
      await axiosAuth().delete(`/sellers/${id}`);
      toast.success("Vendedor eliminado");
      fetch();
    } catch { toast.error("Error al eliminar"); }
  };

  const edit = (s) => {
    setEditingId(s.id);
    setForm({...EMPTY, ...s});
    setShowDialog(true);
  };

  const uploadPhoto = async (e, sellerId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axiosAuth().post(`/sellers/${sellerId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto subida");
      fetch();
    } catch { toast.error("Error al subir foto"); }
  };

  const filtered = sellers.filter(s => s.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="sellers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="sellers-title">Vendedores</h1>
          <p className="text-sm text-slate-500 mt-1">Equipo comercial de venta de vehiculos</p>
        </div>
        <Button onClick={() => { setEditingId(null); setForm({...EMPTY}); setShowDialog(true); }}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-5" data-testid="new-seller-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Vendedor
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Buscar vendedor..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm h-9 border-slate-200" />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-sm" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">No hay vendedores configurados</p>
          <p className="text-xs text-slate-400 mt-1">Crea el primer vendedor para asignarlo a vehiculos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => (
            <Card key={s.id} className="border-slate-200 rounded-sm overflow-hidden" data-testid={`seller-card-${s.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <img src={`${API}/api/sellers/${s.id}/avatar`} alt={s.name} className="w-12 h-12 rounded-full object-cover border border-slate-200 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-slate-900 truncate">{s.name}</h3>
                    <p className="text-xs text-slate-500">{s.title || "Asesor Comercial"}</p>
                    {s.center && <p className="text-xs text-slate-400 truncate">{s.center}</p>}
                  </div>
                  <Badge className={`rounded-sm text-[10px] ${s.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {s.status === "active" ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
                <div className="space-y-1 text-xs text-slate-600 mb-3">
                  {s.phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-slate-400" /> {s.phone}</p>}
                  {s.whatsapp_phone && <p className="flex items-center gap-1.5"><MessageSquare className="w-3 h-3 text-emerald-500" /> {s.whatsapp_phone}</p>}
                  {s.email && <p className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-400" /> {s.email}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => edit(s)} className="rounded-sm text-xs flex-1" data-testid={`edit-seller-${s.id}`}>Editar</Button>
                  <div className="relative">
                    <input type="file" accept="image/*" onChange={e => uploadPhoto(e, s.id)} className="hidden" id={`photo-${s.id}`} />
                    <Button variant="outline" size="sm" onClick={() => document.getElementById(`photo-${s.id}`)?.click()} className="rounded-sm text-xs">
                      <Upload className="w-3 h-3" />
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => del(s.id)} className="rounded-sm text-xs text-red-600">Eliminar</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">{editingId ? "Editar Vendedor" : "Nuevo Vendedor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre completo *</Label>
                <Input value={form.name} onChange={e => f("name", e.target.value)} className="rounded-sm h-9 border-slate-200" data-testid="seller-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Cargo</Label>
                <Input value={form.title} onChange={e => f("title", e.target.value)} className="rounded-sm h-9 border-slate-200" placeholder="Asesor Comercial" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Telefono</Label>
                <Input value={form.phone} onChange={e => f("phone", e.target.value)} className="rounded-sm h-9 border-slate-200" data-testid="seller-phone" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">WhatsApp</Label>
                <Input value={form.whatsapp_phone} onChange={e => f("whatsapp_phone", e.target.value)} className="rounded-sm h-9 border-slate-200" placeholder="+34..." />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</Label>
              <Input value={form.email} onChange={e => f("email", e.target.value)} className="rounded-sm h-9 border-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Centro</Label>
                <Select value={form.center_id || "none"} onValueChange={v => {
                  const c = centers.find(x => x.id === v);
                  f("center_id", v === "none" ? "" : v);
                  f("center", c?.name || "");
                }}>
                  <SelectTrigger className="rounded-sm h-9 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Estado</Label>
                <Select value={form.status} onValueChange={v => f("status", v)}>
                  <SelectTrigger className="rounded-sm h-9 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="inactive">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Orden</Label>
                <Input type="number" value={form.order} onChange={e => f("order", Number(e.target.value))} className="rounded-sm h-9 border-slate-200" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Horario</Label>
                <Input value={form.working_hours_text} onChange={e => f("working_hours_text", e.target.value)} className="rounded-sm h-9 border-slate-200" placeholder="L-V 9:00-19:00" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox checked={form.accepts_leads} onCheckedChange={v => f("accepts_leads", v)} />
              <label className="text-xs text-slate-700">Acepta nuevos leads / asignaciones</label>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Observaciones internas</Label>
              <textarea value={form.notes} onChange={e => f("notes", e.target.value)}
                className="w-full border border-slate-200 rounded-sm p-2 text-sm h-16 resize-none" placeholder="Notas internas..." />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs uppercase flex-1">Cancelar</Button>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold flex-1" data-testid="seller-save">Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
